/**
 * ContractSigningActionEditor — domain-specific action editor for contract_signing context
 * Manages platform, signature type, signatories, approvers.
 * Spec: Sections 12, 13
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthContext';
import { userService } from '../../services/api/userService';

interface Signatory {
  email: string;
  fullName: string;
  notifyEmail?: boolean;
  notifyWhatsapp?: boolean;
  whatsappPhone?: string;
}

interface Approver {
  email: string;
  fullName: string;
  isSigner: boolean;
  notifyEmail?: boolean;
  notifyWhatsapp?: boolean;
  whatsappPhone?: string;
}

interface ContractSigningAction {
  platform: string;
  signatureType?: string;
  signatoryCount?: number;
  signatoryFlow?: string;
  signatories?: Signatory[];
  approvers?: Approver[];
  approverMoment?: string | null;
  approverFlow?: string;
  civilRegistryValidation?: boolean;
}

interface DesignatedSignatory {
  uid: string;
  email: string;
  fullName: string;
}

interface Props {
  action: Record<string, unknown>;
  onChange: (action: Record<string, unknown>) => void;
}

const ContractSigningActionEditor: React.FC<Props> = ({ action, onChange }) => {
  const { t } = useTranslation();
  const { user } = useAuth();

  const [designatedSignatories, setDesignatedSignatories] = useState<DesignatedSignatory[]>([]);
  const [allUserEmails, setAllUserEmails] = useState<Set<string>>(new Set());
  const [externalMode, setExternalMode] = useState<Record<number, boolean>>({});
  const [externalApproverMode, setExternalApproverMode] = useState<Record<number, boolean>>({});

  // Admin's email domain — used to validate external signers
  const adminDomain = user?.email?.split('@')[1]?.toLowerCase() || '';

  // Parse action into typed state
  const data = action as unknown as ContractSigningAction;
  const platform = data.platform || 'fx_plus';
  const signatureType = data.signatureType || 'simple';
  const signatoryFlow = data.signatoryFlow || 'parallel';
  const civilRegistryValidation = data.civilRegistryValidation || false;
  const signatories: Signatory[] = data.signatories || [];
  const approvers: Approver[] = data.approvers || [];
  const approverMoment = data.approverMoment || 'before';
  const approverFlow = data.approverFlow || 'parallel';

  // Fetch designated signatories (users with canSignDocuments: true)
  const loadDesignatedSignatories = useCallback(async () => {
    try {
      const response = await userService.adminListUsers();
      if (response.success && response.data) {
        const filtered = response.data.filter(u =>
          u.canSignDocuments && u.status === 'active'
        );
        setDesignatedSignatories(filtered.map(u => ({
          uid: u.uid,
          email: u.email,
          fullName: u.fullName || `${u.firstName} ${u.lastName}`.trim(),
        })));
        // Build full email set for duplicate detection (all active users, not just signers)
        const allEmails = new Set(
          response.data.filter(u => u.status === 'active').map(u => u.email.toLowerCase())
        );
        setAllUserEmails(allEmails);
      }
    } catch (err) {
      console.error('Failed to load designated signatories:', err);
    }
  }, []);

  useEffect(() => {
    loadDesignatedSignatories();
  }, [loadDesignatedSignatories]);

  // Helper to update the action
  const updateAction = (updates: Partial<ContractSigningAction>) => {
    const newAction = { ...data, ...updates };
    // Derive signatoryCount from signatories array length
    if (newAction.signatories) {
      newAction.signatoryCount = newAction.signatories.length;
    }
    onChange(newAction as unknown as Record<string, unknown>);
  };

  // ── Signatory management ──

  const addSignatory = () => {
    updateAction({ signatories: [...signatories, { email: '', fullName: '' }] });
  };

  const updateSignatory = (index: number, updates: Partial<Signatory>) => {
    const updated = [...signatories];
    updated[index] = { ...updated[index], ...updates };
    updateAction({ signatories: updated });
  };

  const selectSignatory = (index: number, ds: DesignatedSignatory) => {
    updateSignatory(index, { email: ds.email, fullName: ds.fullName });
  };

  const removeSignatory = (index: number) => {
    updateAction({ signatories: signatories.filter((_, i) => i !== index) });
    // Rebuild externalMode indices after removal
    const newExternal: Record<number, boolean> = {};
    Object.entries(externalMode).forEach(([k, v]) => {
      const ki = Number(k);
      if (ki < index) newExternal[ki] = v;
      else if (ki > index) newExternal[ki - 1] = v;
      // ki === index is removed
    });
    setExternalMode(newExternal);
  };

  // Validate an external email
  const validateExternalEmail = (email: string): string | null => {
    if (!email) return null;
    const enteredDomain = email.split('@')[1]?.toLowerCase() || '';
    if (enteredDomain && enteredDomain !== adminDomain) {
      return t('admin.contracts.domainMismatch', 'Email must be from your domain (@{{domain}})', { domain: adminDomain });
    }
    if (allUserEmails.has(email.toLowerCase())) {
      return t('admin.contracts.emailIsRegisteredUser', 'This email belongs to a registered user. Select them from the list.');
    }
    return null;
  };

  // Validate Chilean phone number format: +56 9 XXXX XXXX (spaces optional)
  const validatePhone = (phone: string): string | null => {
    if (!phone) return t('admin.contracts.phoneRequired', 'Phone number is required for WhatsApp.');
    const cleaned = phone.replace(/\s/g, '');
    if (!/^\+569\d{8}$/.test(cleaned)) {
      return t('admin.contracts.phoneInvalid', 'Format: +56 9 XXXX XXXX');
    }
    return null;
  };

  // ── Approver management ──

  const addApprover = () => {
    updateAction({ approvers: [...approvers, { email: '', fullName: '', isSigner: false }] });
  };

  const updateApprover = (index: number, updates: Partial<Approver>) => {
    const updated = [...approvers];
    updated[index] = { ...updated[index], ...updates };
    updateAction({ approvers: updated });
  };

  const selectApprover = (index: number, ds: DesignatedSignatory) => {
    updateApprover(index, { email: ds.email, fullName: ds.fullName });
  };

  const removeApprover = (index: number) => {
    updateAction({ approvers: approvers.filter((_, i) => i !== index) });
    const newExternal: Record<number, boolean> = {};
    Object.entries(externalApproverMode).forEach(([k, v]) => {
      const ki = Number(k);
      if (ki < index) newExternal[ki] = v;
      else if (ki > index) newExternal[ki - 1] = v;
    });
    setExternalApproverMode(newExternal);
  };

  // ── Render ──

  return (
    <div className="contract-signing-action-editor">
      <div className="action-section-title">{t('rules.action', 'Action')}</div>

      {/* Platform */}
      <div className="action-field">
        <label>{t('admin.contracts.platform', 'Signing Platform')}</label>
        <select
          value={platform}
          onChange={e => {
            const newPlatform = e.target.value;
            if (newPlatform !== 'fx_plus') {
              updateAction({
                platform: newPlatform,
                signatureType: undefined,
                signatoryCount: 0,
                signatoryFlow: undefined,
                signatories: [],
                approvers: [],
                approverMoment: undefined,
                approverFlow: undefined,
                civilRegistryValidation: undefined,
              });
            } else {
              updateAction({ platform: newPlatform });
            }
          }}
        >
          <option value="fx_plus">{t('admin.contracts.platformFxPlus', 'FX+')}</option>
          <option value="counterparty_platform">{t('admin.contracts.platformCounterparty', 'Counterparty Platform')}</option>
          <option value="manual">{t('admin.contracts.platformManual', 'Firma Física')}</option>
        </select>
      </div>

      {/* FX+ Platform specific fields */}
      {platform === 'fx_plus' && (
        <>
          {/* Signature Type — hidden for now, only simple signatures implemented. May re-enable when advanced (FEAR API) is built.
          <div className="action-field">
            <label>{t('admin.contracts.signatureType', 'Signature Type')}</label>
            <select
              value={signatureType}
              onChange={e => updateAction({ signatureType: e.target.value })}
            >
              <option value="simple">{t('admin.contracts.signatureTypeSimple', 'Simple')}</option>
              <option value="advanced">{t('admin.contracts.signatureTypeAdvanced', 'Advanced')}</option>
            </select>
          </div>
          */}

          {/* Civil Registry Validation — hidden for now, may be re-enabled for advanced signatures
          <div className="action-field civil-registry-field">
            <label className="civil-registry-label">
              <input
                type="checkbox"
                checked={civilRegistryValidation}
                onChange={e => updateAction({ civilRegistryValidation: e.target.checked })}
              />
              {t('admin.contracts.civilRegistry', 'Require Civil Registry validation')}
              <span className="tooltip-icon" title={t('admin.contracts.civilRegistryTooltip', 'When enabled, signatories must verify their identity through the Civil Registry before signing.')}>?</span>
            </label>
          </div>
          */}

          {/* Signatories (max 2) */}
          <div className="action-field">
            <div className="signatories-header">
              <label>{t('admin.contracts.signatories', 'Signatories')}</label>
              {signatories.length < 2 && (
                <button type="button" className="add-signatory-btn" onClick={addSignatory}>
                  + {t('admin.contracts.addSignatory', 'Add')}
                </button>
              )}
            </div>
            <div className="section-hint">
              {t('admin.contracts.signatoriesHint', 'People who will sign the document.')}
            </div>
            {signatories.length === 0 && (
              <div className="no-signatories-hint">
                {t('admin.contracts.noSignatories', 'Add at least one signatory.')}
              </div>
            )}
            {signatories.map((sig, i) => {
              const usedEmails = signatories.filter((_, j) => j !== i).map(s => s.email).filter(Boolean);
              const approverEmails = approvers.map(a => a.email).filter(Boolean);
              const available = designatedSignatories.filter(ds => !usedEmails.includes(ds.email) && !approverEmails.includes(ds.email));
              const isEditing = externalMode[i] || false;
              const isConfirmedExternal = !isEditing && sig.email && !designatedSignatories.find(ds => ds.email === sig.email);
              const emailError = isEditing ? validateExternalEmail(sig.email) : null;
              const canConfirm = sig.email && sig.fullName && sig.email.includes('@') && !emailError;
              return (
                <div key={i} className="signatory-row-wrapper">
                  {signatoryFlow === 'sequential' && signatories.length > 1 && (
                    <span className="signatory-order">{i + 1}.</span>
                  )}
                  {isEditing ? (
                    <div className="external-signer-form">
                      <div className="external-signer-row">
                        <input
                          type="email"
                          className={`external-signer-email${emailError ? ' has-error' : ''}`}
                          placeholder={t('admin.contracts.externalEmail', 'Email')}
                          value={sig.email}
                          onChange={e => updateSignatory(i, { email: e.target.value })}
                        />
                        <input
                          type="text"
                          className="external-signer-name"
                          placeholder={t('admin.contracts.externalFullName', 'Full name')}
                          value={sig.fullName}
                          onChange={e => updateSignatory(i, { fullName: e.target.value })}
                        />
                        <button
                          type="button"
                          className="external-confirm-btn"
                          disabled={!canConfirm}
                          onClick={() => setExternalMode(prev => ({ ...prev, [i]: false }))}
                        >
                          OK
                        </button>
                        <button type="button" className="remove-btn" onClick={() => removeSignatory(i)}>×</button>
                      </div>
                      {emailError && (
                        <div className="external-signer-error">{emailError}</div>
                      )}
                      <div className="notification-channels">
                        <span className="notification-channel-label">{t('admin.contracts.notifyVia', 'Notify via')}:</span>
                        <label className="notification-channel-option">
                          <input type="checkbox" checked={sig.notifyEmail !== false} onChange={e => updateSignatory(i, { notifyEmail: e.target.checked })} />
                          Email
                        </label>
                        <label className="notification-channel-option">
                          <input type="checkbox" checked={sig.notifyWhatsapp || false} onChange={e => updateSignatory(i, { notifyWhatsapp: e.target.checked, whatsappPhone: e.target.checked ? (sig.whatsappPhone || '') : undefined })} />
                          WhatsApp
                        </label>
                        {sig.notifyWhatsapp && (
                          <>
                            <input
                              type="tel"
                              className={`whatsapp-phone-input${sig.whatsappPhone && validatePhone(sig.whatsappPhone) ? ' has-error' : ''}`}
                              placeholder="+56 9 1234 5678"
                              value={sig.whatsappPhone || ''}
                              onChange={e => updateSignatory(i, { whatsappPhone: e.target.value })}
                            />
                            {sig.whatsappPhone && validatePhone(sig.whatsappPhone) && (
                              <span className="whatsapp-phone-error">{validatePhone(sig.whatsappPhone)}</span>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  ) : isConfirmedExternal ? (
                    <div className="external-confirmed-block">
                      <div className="signatory-row">
                        <span className="external-signer-confirmed">
                          {sig.fullName} ({sig.email})
                        </span>
                        <button type="button" className="remove-btn" onClick={() => removeSignatory(i)}>×</button>
                      </div>
                      <div className="notification-channels">
                        <span className="notification-channel-label">{t('admin.contracts.notifyVia', 'Notify via')}:</span>
                        <label className="notification-channel-option">
                          <input type="checkbox" checked={sig.notifyEmail !== false} onChange={e => updateSignatory(i, { notifyEmail: e.target.checked })} />
                          Email
                        </label>
                        <label className="notification-channel-option">
                          <input type="checkbox" checked={sig.notifyWhatsapp || false} onChange={e => updateSignatory(i, { notifyWhatsapp: e.target.checked, whatsappPhone: e.target.checked ? (sig.whatsappPhone || '') : undefined })} />
                          WhatsApp
                        </label>
                        {sig.notifyWhatsapp && (
                          <>
                            <input
                              type="tel"
                              className={`whatsapp-phone-input${sig.whatsappPhone && validatePhone(sig.whatsappPhone) ? ' has-error' : ''}`}
                              placeholder="+56 9 1234 5678"
                              value={sig.whatsappPhone || ''}
                              onChange={e => updateSignatory(i, { whatsappPhone: e.target.value })}
                            />
                            {sig.whatsappPhone && validatePhone(sig.whatsappPhone) && (
                              <span className="whatsapp-phone-error">{validatePhone(sig.whatsappPhone)}</span>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="signatory-row">
                      <select
                        className="signatory-select"
                        value={sig.email}
                        onChange={e => {
                          if (e.target.value === '__external__') {
                            setExternalMode(prev => ({ ...prev, [i]: true }));
                            updateSignatory(i, { email: '', fullName: '' });
                          } else {
                            const ds = designatedSignatories.find(d => d.email === e.target.value);
                            if (ds) selectSignatory(i, ds);
                          }
                        }}
                      >
                        <option value="">{t('admin.contracts.selectSignatory', '-- Select signatory --')}</option>
                        {available.map(ds => (
                          <option key={ds.uid} value={ds.email}>
                            {ds.fullName} ({ds.email})
                          </option>
                        ))}
                        <option value="__external__">{t('admin.contracts.externalSigner', 'External signer...')}</option>
                      </select>
                      <button type="button" className="remove-btn" onClick={() => removeSignatory(i)}>×</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Signing Flow (when > 1 signatory) */}
          {signatories.length > 1 && (
            <div className="action-field">
              <label>
                {t('admin.contracts.signatoryFlow', 'Signing Flow')}
                <span className="tooltip-icon" title={t('admin.contracts.flowTooltip', 'Parallel: everyone receives the request at the same time. Sequential: they go in order.')}>?</span>
              </label>
              <select
                value={signatoryFlow}
                onChange={e => updateAction({ signatoryFlow: e.target.value })}
              >
                <option value="parallel">{t('admin.contracts.signatoryFlowParallel', 'Parallel')}</option>
                <option value="sequential">{t('admin.contracts.signatoryFlowSequential', 'Sequential')}</option>
              </select>
            </div>
          )}

          {/* Approvers */}
          <div className="action-field">
            <div className="signatories-header">
              <label>{t('admin.contracts.approvers', 'Approvers')}</label>
              {approvers.length < 2 && (
                <button type="button" className="add-signatory-btn" onClick={addApprover}>
                  + {t('admin.contracts.addApprover', 'Add')}
                </button>
              )}
            </div>
            <div className="section-hint">
              {t('admin.contracts.approversHint', 'Review and approve/reject before or after signing.')}
            </div>
            {approvers.map((appr, i) => {
              const usedEmails = approvers.filter((_, j) => j !== i).map(a => a.email).filter(Boolean);
              const signatoryEmails = signatories.map(s => s.email).filter(Boolean);
              const available = designatedSignatories.filter(ds => !usedEmails.includes(ds.email) && !signatoryEmails.includes(ds.email));
              const isEditingAppr = externalApproverMode[i] || false;
              const isConfirmedExternalAppr = !isEditingAppr && appr.email && !designatedSignatories.find(ds => ds.email === appr.email);
              const apprEmailError = isEditingAppr ? validateExternalEmail(appr.email) : null;
              const canConfirmAppr = appr.email && appr.fullName && appr.email.includes('@') && !apprEmailError;
              const isSequential = approverFlow === 'sequential' && approvers.length > 1;
              const isAlsoSignatory = appr.email && signatories.some(s => s.email === appr.email);
              return (
              <div key={i} className="approver-block">
                <div className="signatory-row-wrapper">
                  {isSequential && (
                    <span className="signatory-order">{i + 1}.</span>
                  )}
                  {isEditingAppr ? (
                    <div className="external-signer-form">
                      <div className="external-signer-row">
                        <input
                          type="email"
                          className={`external-signer-email${apprEmailError ? ' has-error' : ''}`}
                          placeholder={t('admin.contracts.externalEmail', 'Email')}
                          value={appr.email}
                          onChange={e => updateApprover(i, { email: e.target.value })}
                        />
                        <input
                          type="text"
                          className="external-signer-name"
                          placeholder={t('admin.contracts.externalFullName', 'Full name')}
                          value={appr.fullName}
                          onChange={e => updateApprover(i, { fullName: e.target.value })}
                        />
                        <button
                          type="button"
                          className="external-confirm-btn"
                          disabled={!canConfirmAppr}
                          onClick={() => setExternalApproverMode(prev => ({ ...prev, [i]: false }))}
                        >
                          OK
                        </button>
                        <button type="button" className="remove-btn" onClick={() => removeApprover(i)}>×</button>
                      </div>
                      {apprEmailError && (
                        <div className="external-signer-error">{apprEmailError}</div>
                      )}
                      <div className="notification-channels">
                        <span className="notification-channel-label">{t('admin.contracts.notifyVia', 'Notify via')}:</span>
                        <label className="notification-channel-option">
                          <input type="checkbox" checked={appr.notifyEmail !== false} onChange={e => updateApprover(i, { notifyEmail: e.target.checked })} />
                          Email
                        </label>
                        <label className="notification-channel-option">
                          <input type="checkbox" checked={appr.notifyWhatsapp || false} onChange={e => updateApprover(i, { notifyWhatsapp: e.target.checked, whatsappPhone: e.target.checked ? (appr.whatsappPhone || '') : undefined })} />
                          WhatsApp
                        </label>
                        {appr.notifyWhatsapp && (
                          <>
                            <input
                              type="tel"
                              className={`whatsapp-phone-input${appr.whatsappPhone && validatePhone(appr.whatsappPhone) ? ' has-error' : ''}`}
                              placeholder="+56 9 1234 5678"
                              value={appr.whatsappPhone || ''}
                              onChange={e => updateApprover(i, { whatsappPhone: e.target.value })}
                            />
                            {appr.whatsappPhone && validatePhone(appr.whatsappPhone) && (
                              <span className="whatsapp-phone-error">{validatePhone(appr.whatsappPhone)}</span>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  ) : isConfirmedExternalAppr ? (
                    <div className="external-confirmed-block">
                      <div className="signatory-row">
                        <span className="external-signer-confirmed">
                          {appr.fullName} ({appr.email})
                        </span>
                        <button type="button" className="remove-btn" onClick={() => removeApprover(i)}>×</button>
                      </div>
                      <div className="notification-channels">
                        <span className="notification-channel-label">{t('admin.contracts.notifyVia', 'Notify via')}:</span>
                        <label className="notification-channel-option">
                          <input type="checkbox" checked={appr.notifyEmail !== false} onChange={e => updateApprover(i, { notifyEmail: e.target.checked })} />
                          Email
                        </label>
                        <label className="notification-channel-option">
                          <input type="checkbox" checked={appr.notifyWhatsapp || false} onChange={e => updateApprover(i, { notifyWhatsapp: e.target.checked, whatsappPhone: e.target.checked ? (appr.whatsappPhone || '') : undefined })} />
                          WhatsApp
                        </label>
                        {appr.notifyWhatsapp && (
                          <>
                            <input
                              type="tel"
                              className={`whatsapp-phone-input${appr.whatsappPhone && validatePhone(appr.whatsappPhone) ? ' has-error' : ''}`}
                              placeholder="+56 9 1234 5678"
                              value={appr.whatsappPhone || ''}
                              onChange={e => updateApprover(i, { whatsappPhone: e.target.value })}
                            />
                            {appr.whatsappPhone && validatePhone(appr.whatsappPhone) && (
                              <span className="whatsapp-phone-error">{validatePhone(appr.whatsappPhone)}</span>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="signatory-row">
                      <select
                        className="signatory-select"
                        value={appr.email}
                        onChange={e => {
                          if (e.target.value === '__external__') {
                            setExternalApproverMode(prev => ({ ...prev, [i]: true }));
                            updateApprover(i, { email: '', fullName: '' });
                          } else {
                            const ds = designatedSignatories.find(d => d.email === e.target.value);
                            if (ds) selectApprover(i, ds);
                          }
                        }}
                      >
                        <option value="">{t('admin.contracts.selectApprover', '-- Select approver --')}</option>
                        {available.map(ds => (
                          <option key={ds.uid} value={ds.email}>
                            {ds.fullName} ({ds.email})
                          </option>
                        ))}
                        <option value="__external__">{t('admin.contracts.externalSigner', 'External signer...')}</option>
                      </select>
                      <button type="button" className="remove-btn" onClick={() => removeApprover(i)}>×</button>
                    </div>
                  )}
                </div>
                {appr.email && (
                  <div className={`approver-details${isSequential ? ' indented' : ''}`}>
                    <label className="approver-is-signer">
                      <input
                        type="checkbox"
                        checked={appr.isSigner}
                        onChange={e => updateApprover(i, { isSigner: e.target.checked })}
                      />
                      {t('admin.contracts.approverIsSigner', 'Also signs')}
                      <span className="tooltip-icon" title={t('admin.contracts.alsoSignsTooltip', 'When checked, this approver also draws a signature on the document. When unchecked, they only approve/reject.')}>?</span>
                    </label>
                    {isAlsoSignatory && (
                      <div className="approver-duplicate-warning">
                        {t('admin.contracts.approverIsAlsoSignatory', 'This person is already a signatory.')}
                      </div>
                    )}
                  </div>
                )}
              </div>
              );
            })}
          </div>

          {/* WhatsApp batch warning */}
          {(signatories.some(s => s.notifyWhatsapp) || approvers.some(a => a.notifyWhatsapp)) && (
            <div className="whatsapp-batch-admin-warning">
              {t('admin.contracts.whatsappBatchWarning', 'Note: WhatsApp notifications are not supported when sending contracts for signature via batch. In batch mode, signatories will need to be sent the signing link manually.')}
            </div>
          )}

          {/* Approver moment and flow */}
          {approvers.length > 0 && (
            <>
              <div className="action-field">
                <label>
                  {t('admin.contracts.approverMoment', 'Approver Moment')}
                  <span className="tooltip-icon" title={t('admin.contracts.approverMomentTooltip', "'Before': approvers must approve before signatories can sign. 'After': signatories sign first, then approvers review.")}>?</span>
                </label>
                <select
                  value={approverMoment}
                  onChange={e => updateAction({ approverMoment: e.target.value })}
                >
                  <option value="before">{t('admin.contracts.approverMomentBefore', 'Before signatories')}</option>
                  <option value="after">{t('admin.contracts.approverMomentAfter', 'After signatories')}</option>
                </select>
              </div>
              {approvers.length > 1 && (
                <div className="action-field">
                  <label>
                    {t('admin.contracts.approverFlow', 'Approver Flow')}
                    <span className="tooltip-icon" title={t('admin.contracts.flowTooltip', 'Parallel: everyone receives the request at the same time. Sequential: they go in order.')}>?</span>
                  </label>
                  <select
                    value={approverFlow}
                    onChange={e => updateAction({ approverFlow: e.target.value })}
                  >
                    <option value="parallel">{t('admin.contracts.signatoryFlowParallel', 'Parallel')}</option>
                    <option value="sequential">{t('admin.contracts.signatoryFlowSequential', 'Sequential')}</option>
                  </select>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Counterparty / Firma Física — no extra fields needed */}
      {platform === 'counterparty_platform' && (
        <div className="platform-hint">
          {t('admin.contracts.counterpartyHint', 'Signing is handled on the counterparty\'s platform. FX+ can be used to review the contract before signing, but the trade must be manually marked as signed in FX+.')}
        </div>
      )}
      {platform === 'manual' && (
        <div className="platform-hint">
          {t('admin.contracts.manualHint', 'The contract will be signed physically, on paper. FX+ can be used to review the contract before signing, but the trade must be manually marked as signed in FX+.')}
        </div>
      )}
    </div>
  );
};

export default ContractSigningActionEditor;
